import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const usersSearchSchema = z.object({
  keyword: z.string().optional(),
  includeDisabled: z.boolean().optional(),
  includeStale: z.boolean().optional(),
  roles: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})

export type UsersSearch = z.infer<typeof usersSearchSchema>

export type Territories = Awaited<ReturnType<typeof getAssignedTerritories>>

async function getUsers(queries: UsersSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.users.$get({ query: stringifyValues(queries) }, { init: { signal } })

  const { users, count } = await res.json()
  return { users, count }
}

export type User = Awaited<ReturnType<typeof getUsers>>['users'][number]

export async function disableUser(userId: string) {
  await getApi().users.disable.$post({ json: { userId } })
}

// Roles are required: an active account with no roles grants nothing, so the backend refuses an
// empty list rather than producing one.
export async function activateUser({ userId, roleIds }: { userId: string; roleIds: number[] }) {
  await getApi().users.activate.$post({ json: { userId, roleIds } })
}

export const getUserPermissions = async (userId: string, signal?: AbortSignal) => {
  const api = getApi()
  const res = await api.users[':userId'].permissions.$get(
    {
      param: { userId },
    },
    { init: { signal } },
  )

  const permissions = await res.json()
  return permissions
}

export const getAssignedTerritories = async (userId: string, signal?: AbortSignal) => {
  const api = getApi()
  const res = await api.users[':userId'].territories.$get(
    {
      param: { userId },
    },
    { init: { signal } },
  )

  const territories = await res.json()
  return territories
}

export function useUsers(queries: UsersSearch) {
  return useQuery({
    queryKey: ['users', queries],
    queryFn: ({ signal }) => getUsers(queries, signal),
  })
}

export function useDisableUser() {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['disable-user'],
    mutationFn: disableUser,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onSuccess: () => {
      toast.success(t('user.update.disable.success'))
    },
    onError: (error) => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: error.message,
      })
    },
  })
}

export function useActivateUser() {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['activate-user'],
    mutationFn: activateUser,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] })
    },
    onSuccess: () => {
      toast.success(t('user.update.activate.success'))
    },
    onError: (error) => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: error.message,
      })
    },
  })
}

export const useUserPermissions = (userId: string) => {
  return useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: ({ signal }) => getUserPermissions(userId, signal),
  })
}

export const useAssignedTerritories = (userId: string) => {
  return useQuery({
    queryKey: ['assigned-territories', userId],
    queryFn: ({ signal }) => getAssignedTerritories(userId, signal),
    enabled: !!userId,
  })
}
